import {
  AffordabilityEvaluationResponse,
  AffordabilityProductKey,
  AffordabilityResponse,
} from '@api/affordability/types';
import { AffordabilityCalculatorForm, FinanceDetails } from '../types';
import { ExtendedFormattedCalculatorResult, FormattedCalculatorResult, ResultsTabsEnum } from './types';
import { ProductTypeEnum } from '@legacy/shared-types/enums/product-type.enum';
import { getProductDetails, nullFiller } from '../helpers';

export const resultsFormatter = (
  responsesArray: AffordabilityEvaluationResponse[],
): ExtendedFormattedCalculatorResult[] => {
  return responsesArray
    .filter(element => element.result?.isPassed)
    .map(responseObj => ({
      repayments: responseObj?.result?.initialMonthlyPayment || 0,
      maxFinancing: responseObj?.result?.fixedMaxFinancingPossible || 0,
      maximumPossible: responseObj?.result?.maximumPossibleResponse
        ? {
            repayments: responseObj?.result?.maximumPossibleResponse?.initialMonthlyPayment || 0,
            maxFinancing: responseObj?.result?.maximumPossibleResponse?.fixedMaxFinancingPossible || 0,
            requestedFinance: responseObj?.result?.maximumPossibleResponse?.financingAmount || 0,
            term: responseObj.product.monthsInitial / 12,
            ltv: responseObj.product.maximumFTV,
            fee: responseObj.product.productFees,
            initialRate: responseObj?.product.initialBaseRate + responseObj?.product.initialMargin,
            ...responseObj?.product,
          }
        : null,
      requestedFinance: responseObj?.result?.financingAmount || 0,
      term: responseObj.product.monthsInitial / 12,
      ltv: responseObj.product.maximumFTV,
      fee: responseObj.product.productFees,
      initialRate: responseObj?.product.initialBaseRate + responseObj?.product.initialMargin,
      ...responseObj?.product,
    }));
};

export const siblingsProductRemover = (responsesArray: AffordabilityEvaluationResponse[]) => {
  const listOfFieldsToCompare: AffordabilityProductKey[] = [
    'monthsInitial',
    'maximumFTV',
    'productType',
    'productRange',
  ];
  const isObjectTheSibling = (
    responseObj: AffordabilityEvaluationResponse,
    responseToCompare: AffordabilityEvaluationResponse,
  ) => listOfFieldsToCompare.every(field => responseObj?.product?.[field] === responseToCompare?.product?.[field]);

  responsesArray.map((responseObj, index) => {
    responsesArray.map((responseToCompare, indexToCompare) => {
      const isSibling = isObjectTheSibling(responseObj, responseToCompare);

      if (index !== indexToCompare && isSibling && responseToCompare.product.equityShare === 0) {
        responsesArray.splice(indexToCompare, 1);
      }
    });
  });

  return responsesArray;
};

export const sortResultsBy = (
  field: keyof ExtendedFormattedCalculatorResult,
  order: 'asc' | 'desc',
  results: ExtendedFormattedCalculatorResult[],
): ExtendedFormattedCalculatorResult[] => {
  if (!results.length || typeof results[0][field] !== 'number') return results;

  return results.sort((a: ExtendedFormattedCalculatorResult, b: ExtendedFormattedCalculatorResult) => {
    const aField = a[field] as number;
    const bField = b[field] as number;

    if (order === 'asc') {
      return aField - bField;
    } else {
      return bField - aField;
    }
  });
};

export const selectResultWith = (
  field: keyof ExtendedFormattedCalculatorResult,
  select: 'minValue' | 'maxValue',
  results: ExtendedFormattedCalculatorResult[],
) => {
  if (!results.length || typeof results[0][field] !== 'number') return null;

  return sortResultsBy(field, select === 'minValue' ? 'asc' : 'desc', results)[0];
};

export const findBestResult = (
  results: ExtendedFormattedCalculatorResult[],
  financeDetails: FinanceDetails,
): ExtendedFormattedCalculatorResult | null => {
  if (
    (!financeDetails.financingAmount && !financeDetails.product) ||
    (financeDetails.financingAmount && results.every(r => r.requestedFinance !== financeDetails.financingAmount))
  ) {
    return null;
  } else {
    if (!financeDetails.product) {
      const result = selectResultWith(
        'repayments',
        'minValue',
        results.filter(
          ({ monthsInitial, productType }) => monthsInitial === 60 && productType === ProductTypeEnum.FIXED,
        ),
      );

      if (result) {
        return result;
      }
    }

    const { productType, productYears } = getProductDetails(financeDetails.product);
    const monthsInitial = productYears ? nullFiller(productYears) * 12 : null;

    return selectResultWith(
      'repayments',
      'minValue',
      financeDetails.product
        ? results.filter(result => result.productType === productType && result.monthsInitial === monthsInitial)
        : results,
    );
  }
};

const resultsInclude5YearFixed = (results: ExtendedFormattedCalculatorResult[]) =>
  results.some(({ monthsInitial, productType }) => monthsInitial === 60 && productType === ProductTypeEnum.FIXED);

export const sortResults = (
  results: ExtendedFormattedCalculatorResult[],
  financeDetails: FinanceDetails,
): ExtendedFormattedCalculatorResult[] => {
  if (financeDetails.financingAmount) {
    if (!financeDetails.product && !resultsInclude5YearFixed(results)) {
      return sortResultsBy('maxFinancing', 'desc', results);
    }

    return sortResultsBy('repayments', 'asc', results);
  } else {
    return sortResultsBy('maxFinancing', 'desc', results);
  }
};

export const getMaximumPossibleResponse = (productOption: ExtendedFormattedCalculatorResult) => {
  if (productOption.maximumPossible) {
    return productOption.maximumPossible;
  }
  return productOption;
};

export const getSelectedResult = (
  financeDetails: FinanceDetails,
  results: ExtendedFormattedCalculatorResult[],
): { bestProduct: ExtendedFormattedCalculatorResult | null; productOptions: ExtendedFormattedCalculatorResult[] } => {
  const maximumPossibleResponses = results.map(getMaximumPossibleResponse);

  return {
    bestProduct: findBestResult(results, financeDetails),
    productOptions: sortResults(maximumPossibleResponses, financeDetails),
  };
};

export enum ResultsScenariosEnum {
  financingAmountAndProductSuccess = 'financingAmountAndProductSuccess',
  financingAmountAndProductSmallerSuccess = 'financingAmountAndProductSmallerSuccess',
  financingAmountAndNoProductSuccess = 'financingAmountAndNoProductSuccess',
  noFinancingAmountAndProductSuccess = 'noFinancingAmountAndProductSuccess',
  noFinancingAmountAndNoProductSuccess = 'noFinancingAmountAndNoProductSuccess',
  unableToHelp = 'unableToHelp',
  techIssue = 'techIssue',
}

export const scenariosWithoutFooter = [ResultsScenariosEnum.unableToHelp, ResultsScenariosEnum.techIssue];

export const getScenario = (
  formFields: AffordabilityCalculatorForm,
  maximumFinancingPossible: number,
  formattedResults: FormattedCalculatorResult[],
) => {
  const { financingAmount, product } = formFields.financeDetails;

  if (formattedResults.length === 0) {
    return ResultsScenariosEnum.unableToHelp;
  }

  if (financingAmount && product) {
    if (financingAmount > maximumFinancingPossible) {
      return ResultsScenariosEnum.financingAmountAndProductSmallerSuccess;
    }
    return ResultsScenariosEnum.financingAmountAndProductSuccess;
  } else if (financingAmount && !product) {
    return ResultsScenariosEnum.financingAmountAndNoProductSuccess;
  } else if (!financingAmount && product) {
    return ResultsScenariosEnum.noFinancingAmountAndProductSuccess;
  } else if (!financingAmount && !product) {
    return ResultsScenariosEnum.noFinancingAmountAndNoProductSuccess;
  }

  return ResultsScenariosEnum.techIssue;
};

export const getHeaderByScenario = (scenario: ResultsScenariosEnum) => {
  switch (scenario) {
    case ResultsScenariosEnum.financingAmountAndProductSuccess:
      return 'affordabilityCalculator.results.header.success';
    case ResultsScenariosEnum.financingAmountAndProductSmallerSuccess:
      return 'affordabilityCalculator.results.header.smallerSuccess';
    case ResultsScenariosEnum.financingAmountAndNoProductSuccess:
      return 'affordabilityCalculator.results.header.financingAmountNoProduct';
    case ResultsScenariosEnum.noFinancingAmountAndProductSuccess:
      return 'affordabilityCalculator.results.header.noFinancingAmountSuccess';
    case ResultsScenariosEnum.noFinancingAmountAndNoProductSuccess:
      return 'affordabilityCalculator.results.header.noFinancingAmountNoProductSuccess';
    case ResultsScenariosEnum.unableToHelp:
      return 'affordabilityCalculator.results.header.unableToHelp';
    case ResultsScenariosEnum.techIssue:
      return 'affordabilityCalculator.results.header.techIssues';
    default:
      return '';
  }
};

export const getMaximumFinancing = (formattedResults: ExtendedFormattedCalculatorResult[]) => {
  return formattedResults.length ? formattedResults.sort((a, b) => b.maxFinancing - a.maxFinancing)[0].maxFinancing : 0;
};

export const filterTabsByScenario = (tab: ResultsTabsEnum, scenario: ResultsScenariosEnum) => {
  return scenario === ResultsScenariosEnum.unableToHelp && tab === ResultsTabsEnum.results ? null : tab;
};

const allowedErrorKeys = [
  'general',
  'maxFinancingImpliedByAffordability',
  'maxFinancingImpliedByProductFTV',
  'maxFinancingImpliedByFTIMultiple',
  'maxFinancingImpliedByUpperLimit',
  'minFinancingImpliedByLowerLimit',
];

export const selectFailedTextOptionKey = (
  affordability: AffordabilityResponse | AffordabilityEvaluationResponse | null | undefined,
) => {
  if (!affordability || !affordability?.result) return 'affordabilityCalculator.results.header.unableToHelp';

  const maxFinancingFailingKey = Object.entries(affordability.result).find(
    ([key, value]) =>
      allowedErrorKeys.includes(key) &&
      key !== 'maximumFinancingPossible' &&
      typeof value === 'number' &&
      Math.round(value) === affordability.result?.maximumFinancingPossible,
  );

  return `affordabilityCalculator.result.failed.text.${maxFinancingFailingKey?.[0] || 'general'}`;
};
